import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  DialogTitle,
  Button,
  InputAdornment,
  Alert,
  useMediaQuery,
  Tooltip,
  List,
  ListSubheader,
  ListItemIcon,
  ListItem,
  ListItemText,
  Switch,
} from '@mui/material';
import {
  ArrowBack,
  Error,
  PictureAsPdf,
  Close,
  CheckCircleOutlineOutlined,
  Warning as WarningIcon,
  DescriptionOutlined,
  PhotoLibraryOutlined,
  AssignmentOutlined,
  Edit,
  Save,
  Cancel,
  Compare,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { withDevice, WithDeviceProps } from '../hooks/withDevice';
import { DocumentImage, Photos, Project, Warning } from '../types';
import PhotoButton from './PhotoButton';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { captureException } from '../utils/captureException';

interface IdentificationProps extends WithDeviceProps {
  project: Project;
  openIdentification: boolean;
  handleClose: () => void;
  handleNextStep: (id: string) => void;
  refetch: () => void;
  onOpenPdf: () => void;
  setProject: (project: Project) => void;
  isSmallScreen: boolean;
  setPdfType: (pdfType: string) => void;
  handleCloseAll: () => void;
}

interface PhotoButtonType {
  type: string;
  id: string;
  label: string;
  status: string;
  warning: boolean;
  photos: Photos[];
}

// Add this component before the Identification component (around line 61)
const NoTransition = React.forwardRef<any, any>(
  function NoTransition(props, ref) {
    const {
      children,
      appear,
      in: inProp,
      timeout,
      onEnter,
      onEntering,
      onEntered,
      onExit,
      onExiting,
      onExited,
      ownerState,
      ...rest
    } = props;
    // Return children directly without wrapper to preserve Dialog's layout
    return <>{children}</>;
  }
);

const Identification: React.FC<IdentificationProps> = ({
  project,
  openIdentification,
  handleClose,
  handleNextStep,
  refetch,
  onOpenPdf,
  setPdfType,
  setProject,
  isSmallScreen,
  handleCloseAll,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [globalDisabled, setGlobalDisabled] = useState(false);
  const [isEditingVin, setIsEditingVin] = useState(false);
  const [vinValue, setVinValue] = useState('');
  const [isEditingElevador, setIsEditingElevador] = useState(false);
  const [elevadorValue, setElevadorValue] = useState('');
  const [isKitSaving, setIsKitSaving] = useState(false);

  const isEditable = project?.editable !== false;

  // Derive photoButtons directly from project to ensure immediate updates
  const photoButtons: PhotoButtonType[] = project ? [
    {
      type: 'ficha' as const,
      id: 'ficha',
      label: t('identification.photo.ficha'),
      status: project?.ficha_rosroca?.status,
      warning: project?.ficha_rosroca?.status === "completed" && project?.ficha_rosroca?.classifier_info.label !== "Ficha RosRoca",
      photos: [{
        zone: t('identification.photo.ficha'),
        status: project?.ficha_rosroca?.status,
        gcs_path: project?.ficha_rosroca?.gcs_path}] as Photos[],
    },
    {
      type: 'chasis' as const,
      id: 'chasis',
      label: t('identification.photo.chasis'),
      status: project?.chasis_rosroca?.status,
      warning: project?.chasis_rosroca?.status === "completed" && project?.chasis_rosroca?.classifier_info.label !== 'Vestidor',
      photos: [{
        zone: t('identification.photo.chasis'),
        status: project?.chasis_rosroca?.status,
        gcs_path: project?.chasis_rosroca?.gcs_path}] as Photos[],
    },
    {
      type: 'xapa' as const,
      id: 'xapa',
      label: t('identification.photo.xapa'),
      status: project?.xapa_rosroca?.status,
      warning: project?.xapa_rosroca?.status === "completed" && project?.xapa_rosroca?.classifier_info.label !== "Chapa RosRoca",
      photos: [{
        zone: t('identification.photo.xapa'),
        status: project?.xapa_rosroca?.status,
        gcs_path: project?.xapa_rosroca?.gcs_path}] as Photos[],
    },
  ] : [];

  const handleWarningClick = async (id: string) => {
    const warning = project?.warning_inspeccion?.find(warning => warning.id === id);
    if (warning) {
      warning.check = !warning.check;
    }
    const idToken = await user?.getIdToken();
    if (!idToken) {
      setError(t('identification.noTokenFound'));
      return;
    }
    axios.put(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
      ["warning_inspeccion" as keyof Project]: project?.warning_inspeccion,
    }, {
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    }).then((response) => {
      console.log("response", response);
    }).catch((error) => {
      captureException(error, { tags: { feature: 'identification' }, extra: { action: 'handleToggleWarning', warningId: warning?.id, project_code: project?.project_code } });
      setError(error.response.data.error || 'Error updating project');
      if (warning) {
        warning.check = !warning.check;
        setProject({
          ...project,
          warning_inspeccion: project?.warning_inspeccion,
        });
      }
    });
    setProject({
      ...project,
      warning_inspeccion: project?.warning_inspeccion,
    });
  };

  const handleToggleIsKit = async () => {
    if (!isEditable) return;
    const prevProject = project;
    const newValue = !project?.is_kit;
    const idToken = await user?.getIdToken();
    if (!idToken) {
      setError(t('identification.noTokenFound'));
      return;
    }
    try {
      setIsKitSaving(true);
      setProject({ ...project, is_kit: newValue });
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`,
        { is_kit: newValue },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      console.log('✅ saved successfully:', response);
      await refetch();
    } catch (err: any) {
      captureException(err, { tags: { feature: 'identification' }, extra: { action: 'handleToggleIsKit', project_code: project?.project_code } });
      setProject(prevProject);
      setError(err?.response?.data?.error || err?.message || 'Error updating is_kit');
    } finally {
      setIsKitSaving(false);
    }
  };

  const handleVinUpdate = async () => {
    if (!vinValue.trim()) {
      setError('El VIN no puede estar vacío');
      return;
    }

    const idToken = await user?.getIdToken();
    if (!idToken) {
      setError(t('identification.noTokenFound'));
      return;
    }

    try {
      const updatedFicha = {
        ...project.ficha_rosroca,
        content: {
          ...project.ficha_rosroca?.content,
          vin: vinValue.trim()
        }
      };

      await axios.put(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
        ficha_rosroca: updatedFicha
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      setProject({
        ...project,
        ficha_rosroca: updatedFicha
      });
      
      setIsEditingVin(false);
      setError(null);
    } catch (error: any) {
      captureException(error, { tags: { feature: 'identification' }, extra: { action: 'handleVinUpdate', project_code: project?.project_code } });
      setError(error.response?.data?.error || 'Error updating VIN');
    }
  };

  const handleElevadorUpdate = async () => {
    if (!elevadorValue.trim()) {
      setError('El Elevador no puede estar vacío');
      return;
    }

    const idToken = await user?.getIdToken();
    if (!idToken) {
      setError(t('identification.noTokenFound'));
      return;
    }

    try {
      const updatedDetallesEquipo = {
        ...project["DETALLES EQUIPO"],
        "Tipo Elevador": {
          ...project["DETALLES EQUIPO"]?.["Tipo Elevador"],
          value: elevadorValue.trim()
        }
      };

      await axios.put(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
        "DETALLES EQUIPO": updatedDetallesEquipo
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      setProject({
        ...project,
        "DETALLES EQUIPO": updatedDetallesEquipo
      });
      
      setIsEditingElevador(false);
      setError(null);
    } catch (error: any) {
      captureException(error, { tags: { feature: 'identification' }, extra: { action: 'handleElevadorUpdate', project_code: project?.project_code } });
      setError(error.response?.data?.error || 'Error updating VIN');
    }
  };

  const handleVinCancel = () => {
    setVinValue(project?.ficha_rosroca?.content?.vin || '');
    setIsEditingVin(false);
  };

  const handleElevadorCancel = () => {
    setElevadorValue(project?.["DETALLES EQUIPO"]?.["Tipo Elevador"]?.value || '');
    setIsEditingElevador(false);
  };

  // Initialize VIN value when project changes
  useEffect(() => {
    if (project?.ficha_rosroca?.content?.vin) {
      setVinValue(project.ficha_rosroca.content.vin);
    }
  }, [project?.ficha_rosroca?.content?.vin]);

  useEffect(() => {
    if (project?.["DETALLES EQUIPO"]?.["Tipo Elevador"]?.value) {
      setElevadorValue(project["DETALLES EQUIPO"]["Tipo Elevador"].value);
    }
  }, [project?.["DETALLES EQUIPO"]?.["Tipo Elevador"]?.value]);


  useEffect(() => {
    const checkOptionsNoFotos = async (
      allCompleted: "completed" | "pending",
      pending: string[] = [],
    ) => {
      const idToken = await user?.getIdToken();
      if (!idToken) {
        setError(t('capture.noTokenFound'));
        return;
      }
      let warning_inspeccion: Warning[] = project.warning_inspeccion?.filter((warning) => ["project", "product", "client"].includes(warning.id)) || [];
      if (allCompleted === "pending") {
        pending.forEach((title) => {
          warning_inspeccion.push({id: title, message: 'Falta capturar: ' + title, check: false});
        });
      }
      axios.put(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
        status_inspeccion: allCompleted ,
        warning_inspeccion: warning_inspeccion,
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      }).then(() => {
        setProject({
          ...project,
          status_inspeccion: allCompleted,
          warning_inspeccion: warning_inspeccion,
        });
      }).catch((error) => {
        captureException(error, { tags: { feature: 'identification' }, extra: { action: 'checkOptionsNoFotos', project_code: project?.project_code } });
        setError(error.response.data.error || t('capture.updateFailed'));
      });
    };

    if (project) {
      let allCompleted = "completed";
      let pending: string[] = [];
      const fieldsToCheck = [
        {key: "ficha_rosroca", label: t('identification.photo.ficha')},
        {key: "chasis_rosroca", label: t('identification.photo.chasis')},
        {key: "xapa_rosroca", label: t('identification.photo.xapa')}];
      for (const field of fieldsToCheck) {
        const documentImage = project[field.key as keyof Project] as DocumentImage;
        if (documentImage === undefined) {
          allCompleted = "pending";
          pending.push(field.label);
        } else if (documentImage?.status === "pending") {
          allCompleted = "pending";
          pending.push(field.label);
        } 
      };
      if (pending.length > 0) {
        allCompleted = "pending";
      }
      if (allCompleted === "completed" && project.status_inspeccion !== "completed") {
        checkOptionsNoFotos("completed");
      } else if (allCompleted === "pending" && project.status_inspeccion !== "pending" || project.warning_inspeccion.filter((warning) => !["project", "product", "client"].includes(warning.id)).length  !== pending.length) {
        checkOptionsNoFotos("pending", pending);
      }
    }
  }, [project]);


  return (
    <Dialog
      fullScreen
      open={openIdentification && project !== null}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer,
        pt: isSmallScreen ? "56px" : "64px",
        pb: "56px",
      }}
      onClose={handleClose}
      slots={{ transition: NoTransition }}
      key={"identification"}
    >
      <DialogTitle sx={{ p: 0, bgcolor: 'background.light' }}>
        <AppBar
          position="fixed"
          sx={{
            // zIndex: (theme) => theme.zIndex.drawer,
            pt: isSmallScreen ? "56px" : "64px",
          }}
          color="inherit"
        >
          <Toolbar sx={{ p: 1 }}>
            <IconButton onClick={handleClose} color="inherit" sx={{ mr: 1 }}><ArrowBack/></IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }} />
            {/* <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('identification.title')}</Typography> */}
            {project?.warning_code_missing && (
              <Tooltip title="Codigo de proyecto no encontrado en informe de lanzamiento" placement="bottom">
                <span>
                  <IconButton sx={{ 
                    mr: 0.5,
                    cursor: 'default',
                    '&.Mui-disabled': {
                      opacity: 1,
                      color: 'warning.main',
                      '& .MuiIconButton-root': {
                        opacity: 1,
                        color: 'warning.main',
                      },
                      '& .MuiSvgIcon-root': {
                        opacity: 1,
                      },
                    },
                      }} disabled>
                    <WarningIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {project?.gcs_series_book_path && (
              <Tooltip title={t('projectList.seriesBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('series');
                }}
                sx={{ mr: 0.5 }}><Compare /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_homologation_book_path && (
              <Tooltip title={t('projectList.homologationBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('homologation');
                }}
                sx={{ mr: 0.5 }}><AssignmentOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_image_book_path && (
              <Tooltip title={t('projectList.imageBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('image_book');
                }}
                sx={{ mr: 0.5 }}><PhotoLibraryOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_report_path && (
              <Tooltip title={t('projectList.reportTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('report');
                }}
                sx={{ mr: 0.5 }}><DescriptionOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_pdf_path && (
              <Tooltip title={t('projectList.launchTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('lanzamiento');
                }}
                sx={{ mr: 0.5 }}><PictureAsPdf /></IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleCloseAll} color="inherit"><Close /></IconButton>
          </Toolbar>
        </AppBar>
        {project && (
          <Box sx={{ py: 3, pl: 2, pr: 2, minHeight: "calc(100vh - 112px - " + (isSmallScreen ? "56px" : "72px") + ")", mt: isSmallScreen ? "56px" : "64px" }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Visual Identification Section */}
              <Box>
                <Stack spacing={2}>
                  {photoButtons.map((button) => (
                    <PhotoButton
                      key={button.type}
                      type={button.type}
                      status={button.status}
                      warning={button.warning}
                      label={button.label}
                      setError={setError}
                      project={project}
                      refetch={refetch}
                      photos={button.photos}
                      isMobile={isSmallScreen}
                      mandatory={true}
                      id={button.id}
                      setProject={setProject}
                      globalDisabled={globalDisabled}
                      setGlobalDisabled={setGlobalDisabled}
                      doQualityCheck={true}
                    />
                  ))}
                </Stack>
              </Box>

              {/* Vehicle Information Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 2,
                    fontSize: 18,
                  }}
                >
                  {t('identification.vehicle.title')}
                </Typography>
                
                <Box
                    component="form"
                    noValidate
                    autoComplete="off"
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                  <TextField
                    label={t('identification.vehicle.project')}
                    value={project.project_code}
                    error={project?.warning_inspeccion?.find(warning => warning.id === "project")?.check === false}
                    helperText={project?.warning_inspeccion?.some(warning => warning.id === "project") ? project?.warning_inspeccion?.find(warning => warning.id === "project")?.message : ''}
                    fullWidth
                    variant="outlined"
                    disabled
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {project?.warning_inspeccion?.find(warning => warning.id === "project") ? project?.warning_inspeccion?.find(warning => warning.id === "project")?.check ? 
                            <IconButton onClick={() => handleWarningClick("project")}><CheckCircleOutlineOutlined sx={{ color: 'success.main' }} /></IconButton> : 
                            <IconButton onClick={() => handleWarningClick("project")}><Error sx={{ color: 'warning.main' }} /></IconButton> : null}
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    }}
                  />
                  
                  <TextField
                    label={t('identification.vehicle.product')}
                    value={project?.ficha_rosroca?.content && project?.ficha_rosroca?.content?.model + " " + project?.ficha_rosroca?.content?.type + " " + project?.ficha_rosroca?.content?.subtype || ''}
                    fullWidth
                    variant="outlined"
                    disabled
                    error={project?.warning_inspeccion?.find(warning => warning.id === "product")?.check === false}
                    helperText={project?.warning_inspeccion?.some(warning => warning.id === "product") ? project?.warning_inspeccion?.find(warning => warning.id === "product")?.message : ''}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {project?.warning_inspeccion?.find(warning => warning.id === "product") ? project?.warning_inspeccion?.find(warning => warning.id === "product")?.check ? 
                            <IconButton onClick={() => handleWarningClick("product")}><CheckCircleOutlineOutlined sx={{ color: 'success.main' }} /></IconButton> : 
                            <IconButton onClick={() => handleWarningClick("product")}><Error sx={{ color: 'warning.main' }} /></IconButton> : null}
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    }}
                  />
                  
                  <TextField
                    label={t('identification.vehicle.chassisSerialNumber')}
                    value={isEditingVin ? vinValue : (project?.ficha_rosroca?.content?.vin || '')}
                    fullWidth
                    variant="outlined"
                    disabled={(!isEditingVin)&&(!isEditable)}
                    onChange={(e) => setVinValue(e.target.value)}
                    slotProps={{
                      input: {
                        endAdornment: isEditable ? (
                          <InputAdornment position="end">
                            {isEditingVin ? (
                              <>
                                <IconButton onClick={handleVinUpdate} size="small" sx={{ mr: 0.5 }}>
                                  <Save sx={{ color: 'success.main' }} />
                                </IconButton>
                                <IconButton onClick={handleVinCancel} size="small">
                                  <Cancel sx={{ color: 'error.main' }} />
                                </IconButton>
                              </>
                            ) : (
                              <IconButton onClick={() => setIsEditingVin(true)} size="small">
                                <Edit sx={{ color: 'primary.main' }} />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ) : null,
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isEditingVin ? 'background.default' : 'background.paper',
                      },
                    }}
                  />
                  
                  <TextField
                    label={t('identification.vehicle.clientName')}
                    value={project["DETALLES DE LA ORDEN"]?.["Cliente"] || ''}
                    fullWidth
                    error={project?.warning_inspeccion?.find(warning => warning.id === "client")?.check === false}
                    helperText={project?.warning_inspeccion?.some(warning => warning.id === "client") ? project?.warning_inspeccion?.find(warning => warning.id === "client")?.message : ''}
                    variant="outlined"
                    disabled
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {project?.warning_inspeccion?.find(warning => warning.id === "client") ? project?.warning_inspeccion?.find(warning => warning.id === "client")?.check ? 
                            <IconButton onClick={() => handleWarningClick("client")}><CheckCircleOutlineOutlined sx={{ color: 'success.main' }} /></IconButton> : 
                            <IconButton onClick={() => handleWarningClick("client")}><Error sx={{ color: 'warning.main' }} /></IconButton> : null}
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    }}
                  />

                  <TextField
                    label={t('identification.vehicle.units')}
                    value={project["DETALLES DE LA ORDEN"]?.["Unidades"] || ''}
                    fullWidth
                    variant="outlined"
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    }}
                  />

                  <TextField
                    label={t('identification.vehicle.elevador')}
                    value={isEditingElevador ? elevadorValue : (project?.["DETALLES EQUIPO"]?.["Tipo Elevador"]?.value || '')}
                    fullWidth
                    variant="outlined"
                    disabled={(!isEditingElevador)&&(!isEditable)}
                    onChange={(e) => setElevadorValue(e.target.value)}
                    slotProps={{
                      input: {
                        endAdornment: isEditable ? (
                          <InputAdornment position="end">
                            {isEditingElevador ? (
                              <>
                                <IconButton onClick={handleElevadorUpdate} size="small" sx={{ mr: 0.5 }}>
                                  <Save sx={{ color: 'success.main' }} />
                                </IconButton>
                                <IconButton onClick={handleElevadorCancel} size="small">
                                  <Cancel sx={{ color: 'error.main' }} />
                                </IconButton>
                              </>
                            ) : (
                              <IconButton onClick={() => setIsEditingElevador(true)} size="small">
                                <Edit sx={{ color: 'primary.main' }} />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ) : null,
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isEditingElevador ? 'background.default' : 'background.paper',
                      },
                    }}
                  />
                  
                  <List
                    sx={{ width: '100%', bgcolor: 'background.paper', p: 0, m: 0, height: "56px", borderRadius: "8px", border: "1px solid rgba(189, 189, 189, 0.87)" }}
                  >
                    <ListItem sx={{ height: "56px" }}>
                      <ListItemText id="switch-list-label-wifi" primary="Kit" />
                      <Switch
                        edge="end"
                        onChange={() => handleToggleIsKit()}
                        checked={project?.is_kit || false}
                        inputProps={{
                          'aria-labelledby': 'switch-list-label-wifi',
                        }}
                        color="secondary"
                        disabled={!isEditable || isKitSaving}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Box>
              <Button variant="contained" color="primary" size="large" onClick={() => handleNextStep('vehicle-identification')}>{t('identification.vehicle.next')}</Button>
            </Box>
          </Box>
        )}
        
      </DialogTitle>
      {error && (
        <Alert severity="error" sx={{ borderRadius: '0px !important', fontSize: '0.875rem', zIndex: (theme) => theme.zIndex.drawer + 200, position: 'sticky', bottom: 0, left: 0, right: 0 }}>
          {error}
          <IconButton onClick={() => setError(null)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Alert>
      )}
    </Dialog>
  );
};

export default withDevice(React.memo(Identification));
